import { useEffect, useMemo, useRef, useState } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X, Pipette, Plus, Minus } from 'lucide-react';
import { buildColorRangeMask, type ColorRangeMode, type RgbSample } from '../lib/color-range';

/** Color Range modal — Tier 1 Group D.
 *
 *  The source is passed as an `HTMLCanvasElement` (NOT an
 *  `ImageData`) so we never need to push multi-MB pixel buffers
 *  around. Live preview runs at a downscaled resolution via a
 *  GPU-accelerated `drawImage` blit; per-pixel reads (`getImageData`)
 *  happen only on the small preview buffer for live computation and
 *  at the user's commit for the full-resolution Apply pass.
 *
 *  Architecture (matches AdjustmentsModal):
 *    - On open, the source canvas is downscaled once into a
 *      `previewCanvas` of at most PREVIEW_MAX longest side. A single
 *      `getImageData` on that small canvas yields `previewImageData`
 *      for the live mask compute.
 *    - On sample click, we map the click coord to source-space and
 *      read a 1×1 ImageData from the source canvas — cheap regardless
 *      of source size.
 *    - On Apply, we read the source canvas's full ImageData ONCE,
 *      run `buildColorRangeMask` at full res, threshold to binary,
 *      and emit the result canvas. The user accepts the wait at this
 *      single point.
 *
 *  Without this architecture, a 4K source on a slow machine spent
 *  several seconds in synchronous `putImageData`/`getImageData` per
 *  parameter tick — the UI froze. See PR thread for the regression
 *  history. */
interface ColorRangeDialogProps {
  open: boolean;
  /** Canvas pre-drawn with the sampling source at its image-rect
   *  dimensions. Captured at open-time so subsequent edits to the
   *  underlying canvas don't shift the preview mid-modal. */
  source: HTMLCanvasElement | null;
  onClose: () => void;
  /** Apply — receives the final alpha-mask canvas sized to the
   *  source dims. White = selected, transparent = not. */
  onApply: (maskCanvas: HTMLCanvasElement) => void;
}

type Eyedropper = 'replace' | 'add' | 'subtract';

const MODE_OPTIONS: { value: ColorRangeMode; label: string }[] = [
  { value: 'sampled', label: 'Sampled Colors' },
  { value: 'reds', label: 'Reds' },
  { value: 'yellows', label: 'Yellows' },
  { value: 'greens', label: 'Greens' },
  { value: 'cyans', label: 'Cyans' },
  { value: 'blues', label: 'Blues' },
  { value: 'magentas', label: 'Magentas' },
  { value: 'highlights', label: 'Highlights' },
  { value: 'midtones', label: 'Midtones' },
  { value: 'shadows', label: 'Shadows' },
];

const PREVIEW_MAX = 480;

export function ColorRangeDialog({ open, source, onClose, onApply }: ColorRangeDialogProps) {
  const [mode, setMode] = useState<ColorRangeMode>('sampled');
  const [samples, setSamples] = useState<RgbSample[]>([]);
  const [fuzziness, setFuzziness] = useState(50);
  const [eyedropper, setEyedropper] = useState<Eyedropper>('replace');

  const previewCanvasRef = useRef<HTMLCanvasElement | null>(null);

  // Reset working state on open. Without this re-opening the dialog
  // carries samples from a prior session — confusing.
  useEffect(() => {
    if (open) {
      setMode('sampled');
      setSamples([]);
      setFuzziness(50);
      setEyedropper('replace');
    }
  }, [open]);

  // Build the downscaled preview + supporting ImageData once per
  // source change. drawImage downscale is GPU-accelerated; the
  // `getImageData` is on the small preview canvas (≤PREVIEW_MAX
  // longest side) so it's cheap regardless of source size.
  const preview = useMemo(() => {
    if (!source) return null;
    const longest = Math.max(source.width, source.height);
    const scale = longest > PREVIEW_MAX ? PREVIEW_MAX / longest : 1;
    const w = Math.max(1, Math.round(source.width * scale));
    const h = Math.max(1, Math.round(source.height * scale));

    const previewCanvas = document.createElement('canvas');
    previewCanvas.width = w;
    previewCanvas.height = h;
    const pctx = previewCanvas.getContext('2d', { willReadFrequently: true });
    if (!pctx) return null;
    // GPU-accelerated downscale — fast even for 8K sources.
    pctx.drawImage(source, 0, 0, w, h);
    const previewImageData = pctx.getImageData(0, 0, w, h);
    return { width: w, height: h, scale, sourceCanvas: previewCanvas, previewImageData };
  }, [source]);

  // Recompute the visible preview when inputs change. All work
  // bounded by preview dimensions (≤480×480 ≈ 230k pixels) so this
  // stays sub-frame even with multiple samples + slider drag.
  //
  // Defer to rAF so the first draw happens AFTER Radix's portal
  // mount has fully settled. Without this, on first open the
  // effect runs before the canvas's drawing surface is wired up
  // (a known timing race with refs inside `DialogPrimitive.Portal`)
  // — leaving a blank preview until the user interacts with the
  // canvas and triggers a re-render that fires the effect again.
  useEffect(() => {
    if (!open || !preview) return;
    const id = requestAnimationFrame(() => {
      const ctx = previewCanvasRef.current?.getContext('2d');
      if (!ctx) return;
      const mask = buildColorRangeMask(preview.previewImageData, {
        mode,
        samples,
        fuzziness,
      });
      ctx.clearRect(0, 0, preview.width, preview.height);
      ctx.drawImage(preview.sourceCanvas, 0, 0);
      // Red 50% tint on selected pixels — red where mask is high.
      const tint = ctx.createImageData(preview.width, preview.height);
      const td = tint.data;
      for (let i = 0; i < mask.length; i++) {
        const v = mask[i];
        const o = i * 4;
        td[o] = 255;
        td[o + 1] = 48;
        td[o + 2] = 48;
        td[o + 3] = Math.round(v * 0.5);
      }
      const tintScratch = document.createElement('canvas');
      tintScratch.width = preview.width;
      tintScratch.height = preview.height;
      tintScratch.getContext('2d')?.putImageData(tint, 0, 0);
      ctx.drawImage(tintScratch, 0, 0);
    });
    return () => cancelAnimationFrame(id);
  }, [open, preview, mode, samples, fuzziness]);

  const handlePreviewClick = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!source || !preview) return;
    if (mode !== 'sampled') return;
    const target = e.currentTarget;
    const rect = target.getBoundingClientRect();
    // Map click coord → source-space coord. Read 1×1 from the
    // source canvas — cheap regardless of source size.
    const px = ((e.clientX - rect.left) / rect.width) * source.width;
    const py = ((e.clientY - rect.top) / rect.height) * source.height;
    const x = Math.max(0, Math.min(source.width - 1, Math.round(px)));
    const y = Math.max(0, Math.min(source.height - 1, Math.round(py)));
    const sctx = source.getContext('2d', { willReadFrequently: true });
    if (!sctx) return;
    const pix = sctx.getImageData(x, y, 1, 1).data;
    const sample: RgbSample = { r: pix[0], g: pix[1], b: pix[2] };
    if (eyedropper === 'replace') {
      setSamples([sample]);
      setEyedropper('add');
    } else if (eyedropper === 'add') {
      setSamples((s) => [...s, sample]);
    } else {
      setSamples((s) => {
        if (s.length === 0) return s;
        let bestIdx = 0;
        let bestDist = Infinity;
        for (let i = 0; i < s.length; i++) {
          const dr = s[i].r - sample.r;
          const dg = s[i].g - sample.g;
          const db = s[i].b - sample.b;
          const d = dr * dr + dg * dg + db * db;
          if (d < bestDist) {
            bestDist = d;
            bestIdx = i;
          }
        }
        return [...s.slice(0, bestIdx), ...s.slice(bestIdx + 1)];
      });
    }
  };

  const handleApply = () => {
    if (!source || !preview) {
      onClose();
      return;
    }
    // Apply re-uses the LIVE PREVIEW mask the user just saw —
    // we never re-run `buildColorRangeMask` at source resolution.
    // The per-pixel HSL math is by far the slowest step (8M
    // iterations on a 4K source); doing it at preview-resolution
    // (≤480px longest side, ~150k pixels) keeps Apply responsive
    // even for 8K sources.
    //
    // Precision trade-off: the marquee boundary lands at the
    // preview's effective pixel grid (1 preview-px = N source-px).
    // For Color Range — which has soft hue/sat boundaries by
    // nature — this is indistinguishable from a full-res selection
    // after the contour tracer runs. Refine Edge is the path for
    // pixel-exact silhouettes.
    const previewMask = buildColorRangeMask(preview.previewImageData, {
      mode,
      samples,
      fuzziness,
    });

    // Render the preview mask to a preview-sized alpha canvas with
    // pre-thresholded alpha (binary selection — no partial alphas).
    const previewMaskCanvas = document.createElement('canvas');
    previewMaskCanvas.width = preview.width;
    previewMaskCanvas.height = preview.height;
    const pctx = previewMaskCanvas.getContext('2d', { willReadFrequently: true });
    if (pctx) {
      const id = pctx.createImageData(preview.width, preview.height);
      const d = id.data;
      for (let i = 0; i < previewMask.length; i++) {
        const a = previewMask[i] >= 127 ? 255 : 0;
        const o = i * 4;
        d[o] = 255;
        d[o + 1] = 255;
        d[o + 2] = 255;
        d[o + 3] = a;
      }
      pctx.putImageData(id, 0, 0);
    }

    // GPU-accelerated upscale to source dims with bilinear
    // smoothing so the marquee outline doesn't end up as visible
    // 8-pixel stair-steps. setSelectionFromMask re-reads the
    // upscaled alpha and thresholds again (>127) when building the
    // binary mask, so the bilinear's intermediate gray values
    // resolve back to a crisp edge.
    const maskCanvas = document.createElement('canvas');
    maskCanvas.width = source.width;
    maskCanvas.height = source.height;
    const mctx = maskCanvas.getContext('2d');
    if (mctx) {
      mctx.imageSmoothingEnabled = true;
      mctx.imageSmoothingQuality = 'high';
      mctx.drawImage(previewMaskCanvas, 0, 0, source.width, source.height);
    }
    onApply(maskCanvas);
    onClose();
  };

  const sampledMode = mode === 'sampled';
  const samplesValid = !sampledMode || samples.length > 0;

  return (
    <DialogPrimitive.Root
      open={open}
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
    >
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/60 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <DialogPrimitive.Content className="fixed left-[50%] top-[50%] z-50 translate-x-[-50%] translate-y-[-50%] w-[calc(100vw-2rem)] max-w-md bg-background border shadow-lg sm:rounded-lg max-h-[90vh] overflow-y-auto duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95">
          <div className="flex flex-col">
            <div className="sticky top-0 z-10 bg-background px-6 pt-6 pb-3 select-none flex flex-col space-y-1.5">
              <DialogPrimitive.Title className="text-lg font-semibold leading-none tracking-tight">
                Color Range
              </DialogPrimitive.Title>
              <DialogPrimitive.Description className="sr-only">
                Select pixels by color or tonal range. Click the preview to sample colors, adjust
                the fuzziness, then Apply to create a selection.
              </DialogPrimitive.Description>
              <DialogPrimitive.Close className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2">
                <X className="h-4 w-4" />
                <span className="sr-only">Close</span>
              </DialogPrimitive.Close>
            </div>
            <div className="px-6 pb-6 pt-1 space-y-4">
              {!source && (
                <p className="text-xs text-muted-foreground">
                  No image loaded — open one to use Color Range.
                </p>
              )}
              {source && (
                <>
                  <label className="flex items-center gap-2 text-xs">
                    <span className="text-muted-foreground w-16">Select</span>
                    <select
                      value={mode}
                      onChange={(e) => setMode(e.target.value as ColorRangeMode)}
                      className="flex-1 bg-background border border-border rounded px-2 py-1 text-xs"
                    >
                      {MODE_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </label>

                  {sampledMode && (
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground w-16">Sample</span>
                      <div className="flex gap-1 flex-1">
                        <button
                          type="button"
                          title="Replace samples"
                          onClick={() => setEyedropper('replace')}
                          className={`p-1.5 rounded border ${
                            eyedropper === 'replace'
                              ? 'border-primary bg-primary/10 text-primary'
                              : 'border-border text-foreground hover:bg-accent'
                          }`}
                        >
                          <Pipette className="h-3.5 w-3.5" />
                        </button>
                        <button
                          type="button"
                          title="Add to samples"
                          onClick={() => setEyedropper('add')}
                          className={`p-1.5 rounded border ${
                            eyedropper === 'add'
                              ? 'border-primary bg-primary/10 text-primary'
                              : 'border-border text-foreground hover:bg-accent'
                          }`}
                        >
                          <Plus className="h-3.5 w-3.5" />
                        </button>
                        <button
                          type="button"
                          title="Subtract from samples"
                          onClick={() => setEyedropper('subtract')}
                          className={`p-1.5 rounded border ${
                            eyedropper === 'subtract'
                              ? 'border-primary bg-primary/10 text-primary'
                              : 'border-border text-foreground hover:bg-accent'
                          }`}
                        >
                          <Minus className="h-3.5 w-3.5" />
                        </button>
                        <span className="ml-2 text-xs text-muted-foreground tabular-nums self-center">
                          {samples.length} sample{samples.length === 1 ? '' : 's'}
                        </span>
                      </div>
                    </div>
                  )}

                  <label className="flex items-center gap-2 text-xs">
                    <span className="text-muted-foreground w-16">Fuzziness</span>
                    <input
                      type="range"
                      min={0}
                      max={200}
                      value={fuzziness}
                      onChange={(e) => setFuzziness(Number(e.target.value))}
                      className="flex-1"
                    />
                    <span className="w-8 text-right tabular-nums">{fuzziness}</span>
                  </label>

                  <div className="flex justify-center bg-[#1a1a1a] rounded p-2">
                    <canvas
                      ref={previewCanvasRef}
                      width={preview?.width ?? 0}
                      height={preview?.height ?? 0}
                      onClick={handlePreviewClick}
                      className={sampledMode ? 'cursor-crosshair' : 'cursor-default'}
                      style={{
                        imageRendering: 'pixelated',
                        maxWidth: '100%',
                        height: 'auto',
                      }}
                    />
                  </div>

                  {sampledMode && samples.length === 0 && (
                    <p className="text-[11px] text-muted-foreground">
                      Click on the preview to pick a color to select.
                    </p>
                  )}
                </>
              )}

              <div className="flex gap-2 justify-end pt-2 border-t border-border">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-3 py-1.5 rounded border border-border text-xs hover:bg-accent"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleApply}
                  disabled={!source || !samplesValid}
                  className="px-3 py-1.5 rounded bg-primary text-primary-foreground text-xs hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Apply
                </button>
              </div>
            </div>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
